import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Trash2, Loader2, Mail, Download, Info, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

export default function SubscribersManager() {
  const qc = useQueryClient();
  const [sending, setSending] = useState(false);
  const { data: subs, isLoading } = useQuery({
    queryKey: ["admin-subscribers"],
    queryFn: () => api.get("/admin/subscribers").then((r) => r.data),
  });

  const list = subs || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-subscribers"] });

  const sendNewsletter = async () => {
    if (!window.confirm(`Изпращане на седмичния бюлетин до ${list.length} абоната?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/send");
      if (data.sent > 0) toast.success(data.message);
      else toast.warning(`${data.message} Проверете верифицирания подател в SendGrid.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const del = async (s) => {
    if (!window.confirm(`Премахване на абоната ${s.email}?`)) return;
    try {
      await api.delete(`/admin/subscribers/${s.id}`);
      toast.success("Абонатът е премахнат.");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = () => {
    if (list.length === 0) return;
    const csv = "email,date\n" + list.map((s) => `${s.email},${s.created_at}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "abonati-buletin.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-6" data-testid="subscribers-manager">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Абонати за бюлетина</h2>
          <p className="text-xs text-slate-500">{list.length} записани имейла</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={sendNewsletter}
            disabled={list.length === 0 || sending}
            data-testid="send-newsletter-btn"
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white hover:bg-[#0041A8] disabled:opacity-40"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Изпрати бюлетин сега
          </button>
          <button
            onClick={exportCsv}
            disabled={list.length === 0}
            data-testid="export-subscribers-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            <Download className="h-4 w-4" />
            Експорт CSV
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Тук се събират имейлите на абонатите. Автоматичното изпращане на седмичния
          бюлетин изисква имейл услуга (напр. SendGrid/Resend) — може да се включи допълнително.
          Междувременно можете да експортирате списъка.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center text-slate-500">
          Все още няма абонати. Блокът за записване е активен на началната страница.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="divide-y divide-slate-100">
            {list.map((s) => (
              <div key={s.id} className="flex items-center gap-3 p-4" data-testid={`subscriber-row-${s.id}`}>
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Mail className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-900">{s.email}</div>
                  <div className="text-xs text-slate-500">Записан на {formatDateTime(s.created_at)}</div>
                </div>
                <button
                  onClick={() => del(s)}
                  data-testid={`delete-subscriber-${s.id}`}
                  className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"
                  aria-label="Премахни"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
